import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import { Dialog, Typography, CircularProgress, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CropFreeIcon from "@material-ui/icons/CropFree";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import {
  VERDE,
  modalPapel,
  modalHero,
  modalHeroLinha,
  modalHeroIcone,
  modalHeroEtiqueta,
  modalHeroTitulo,
  modalHeroSub,
  modalFechar,
  modalCorpo,
} from "../../utils/estiloNavy";

const PASSOS = [
  "Abra o WhatsApp no celular deste número.",
  "Toque em ⋮ (ou Configurações) › Aparelhos conectados.",
  "Toque em Conectar um aparelho e aponte a câmera para o código.",
];

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    papel: modalPapel(theme, 720),
    hero: modalHero(theme),
    heroLinha: modalHeroLinha,
    heroIcone: modalHeroIcone,
    heroEtiqueta: modalHeroEtiqueta,
    heroTitulo: modalHeroTitulo,
    heroSub: modalHeroSub,
    fechar: { ...modalFechar, marginLeft: "auto", alignSelf: "flex-start" },
    corpo: {
      ...modalCorpo(theme),
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(3),
      padding: theme.spacing(3),
      [theme.breakpoints.down("xs")]: { flexDirection: "column" },
    },
    passos: { flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
    passo: {
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      padding: "12px 14px",
      borderRadius: 14,
      background: escuro ? "rgba(255,255,255,0.04)" : "#fff",
      border: `1px solid ${escuro ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)"}`,
      animation: "iaSubir .35s ease backwards",
    },
    numero: {
      width: 26,
      height: 26,
      borderRadius: 8,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 800,
      color: "#fff",
      background: `linear-gradient(135deg, #2dd4bf, ${VERDE})`,
    },
    passoTexto: { fontSize: "0.86rem", color: theme.palette.text.primary, lineHeight: 1.45 },
    qrCaixa: {
      background: "#fff",
      padding: 18,
      borderRadius: 20,
      boxShadow: "0 0 0 1px rgba(13,148,136,0.25), 0 18px 40px rgba(15,23,42,0.18)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      width: 264,
      height: 264,
      flexShrink: 0,
    },
    cantos: {
      position: "absolute",
      inset: 6,
      pointerEvents: "none",
      background:
        `linear-gradient(${VERDE},${VERDE}) top left/22px 3px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) top left/3px 22px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) top right/22px 3px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) top right/3px 22px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) bottom left/22px 3px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) bottom left/3px 22px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) bottom right/22px 3px no-repeat,` +
        `linear-gradient(${VERDE},${VERDE}) bottom right/3px 22px no-repeat`,
    },
    varredura: {
      position: "absolute",
      left: 0,
      width: "100%",
      height: 2,
      background: VERDE,
      boxShadow: `0 0 12px 2px ${VERDE}`,
      animation: "$scan 2.5s infinite linear",
    },
    "@keyframes scan": {
      "0%": { top: "0%" },
      "50%": { top: "100%" },
      "100%": { top: "0%" },
    },
    carregando: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      color: "#64748b",
      fontSize: "0.85rem",
    },
  };
});

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const socket = openSocket();

    const onWhatsappsession = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on("whatsappSession", onWhatsappsession);

    return () => {
      socket.off("whatsappSession", onWhatsappsession);
    };
  }, [whatsAppId, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: classes.papel }}>
      <div className={classes.hero}>
        <div className={classes.heroLinha}>
          <div className={classes.heroIcone}>
            <CropFreeIcon />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className={classes.heroEtiqueta}>Conectar número</div>
            <div className={classes.heroTitulo}>Leia o QR Code</div>
            <div className={classes.heroSub}>O código se renova sozinho. Esta janela fecha quando conectar.</div>
          </div>
          <IconButton size="small" onClick={onClose} className={classes.fechar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className={classes.corpo}>
        <div className={classes.passos}>
          {PASSOS.map((p, i) => (
            <div key={i} className={classes.passo} style={{ animationDelay: `${i * 70}ms` }}>
              <span className={classes.numero}>{i + 1}</span>
              <Typography className={classes.passoTexto}>{p}</Typography>
            </div>
          ))}
          <Typography variant="caption" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <PhoneIphoneIcon style={{ fontSize: 15 }} />
            Use o celular do chip que vai atender por esta conexão.
          </Typography>
        </div>

        <div className={classes.qrCaixa}>
          {qrCode ? (
            <>
              <QRCode value={qrCode} size={228} />
              <div className={classes.cantos} />
              <div className={classes.varredura} />
            </>
          ) : (
            <div className={classes.carregando}>
              <CircularProgress size={38} thickness={4} style={{ color: VERDE }} />
              Gerando QR Code...
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
